/**
 * Escape inside a dialog closes the innermost thing that is open: a Select's
 * list, a menu, a popover or a suggestion list, and only then the dialog.
 *
 * Radix stacks its layers so only the topmost one answers Escape, but only
 * layers built from the same copy of its layer code share that stack. The
 * dialog primitives resolve to one copy and the select, popover and menu
 * primitives to another, so each thought it was on top. Escape on an open
 * Select closed the whole dialog, and the two then restored the page's pointer
 * events in the wrong order, which left every click blocked until a reload.
 *
 * So the dialog keeps its own stack. A popup inside it registers while it is
 * open; the dialog's Escape handler, which runs first, closes the newest one
 * and stands down. A popup that is not a Radix primitive (a combobox's own
 * list) registers with `useInnerLayer`.
 */
import * as React from "react";

export interface InnerLayers {
  /** Register an open popup's close; returns the unregister. */
  add(close: () => void): () => void;
  /** Close the newest open popup. False when none is open. */
  closeTop(): boolean;
}

export const InnerLayerContext = React.createContext<InnerLayers | null>(null);

/** The stack a dialog, sheet or alert dialog keeps for its popups. */
export function useInnerLayerStack(): InnerLayers {
  const stack = React.useRef<Array<() => void>>([]);
  return React.useMemo(
    () => ({
      add(close) {
        stack.current = [...stack.current, close];
        return () => {
          stack.current = stack.current.filter((c) => c !== close);
        };
      },
      closeTop() {
        const top = stack.current[stack.current.length - 1];
        if (!top) return false;
        top();
        return true;
      },
    }),
    [],
  );
}

/** While `open`, Escape in the enclosing dialog calls `close` instead. */
export function useInnerLayer(open: boolean, close: () => void): void {
  const layers = React.useContext(InnerLayerContext);
  const latest = React.useRef(close);
  latest.current = close;
  React.useEffect(() => {
    if (!open || !layers) return;
    return layers.add(() => latest.current());
  }, [open, layers]);
}

/**
 * Open state for a popup primitive, controlled or not, registered with the
 * enclosing dialog while it is open.
 */
export function useInnerLayerOpen(
  openProp: boolean | undefined,
  defaultOpen: boolean | undefined,
  onOpenChange: ((open: boolean) => void) | undefined,
): [boolean, (open: boolean) => void] {
  const [own, setOwn] = React.useState(defaultOpen ?? false);
  const open = openProp ?? own;
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (openProp === undefined) setOwn(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange],
  );
  useInnerLayer(open, () => setOpen(false));
  return [open, setOpen];
}
